import type { NextPage, GetStaticPropsContext } from 'next'
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Link,
  Text,
  useStyleConfig,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { PromotionSection } from '@/components/PromotionSection'
import { BriefCaseIcon } from '@/components/icons/BriefCaseIcon'
import { RobotIcon } from '@/components/icons/RobotIcon'
import { CommunicationChannelIcon } from '@/components/icons/CommunicationChannelIcon'
import { SUPPORT_PROCUREMENT_SERVICE_URL } from '@/constants'
import { useTranslations } from 'next-intl'
import SeoHead from '@/components/seo-head'

const ProcurementServicePage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const t = useTranslations()

  return (
    <>
      <SeoHead
        title={t('procurement-service.seo.title')}
        breadcrumbTitle={t('procurement-service.seo.breadcrumb')}
        description={t('procurement-service.seo.description')}
      />
      <Box __css={styles} overflow="hidden">
        <Hero />
        <HostingPlacements />
        <ContactOurTeam />
      </Box>
    </>
  )
}

const Hero = () => {
  const t = useTranslations()
  return (
    <Box
      height={{ md: '535px', lg: '580px' }}
      bgImage={{ base: 'none', md: '/img/hero-procurement.png' }}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="bottom"
    >
      <Center width="100%" height="100%">
        <ContainerBase p={{ base: '32px 16px', lg: '0 33px' }}>
          <Flex justifyContent={{ base: 'center', lg: 'flex-end' }}>
            <Box
              bg="white"
              borderRadius="8px"
              textAlign={{ base: 'center', lg: 'left' }}
              boxShadow={{
                base: 'none',
                md: '0px 8px 16px -6px rgba(24, 39, 75, 0.08), 0px 20px 50px rgba(0, 0, 0, 0.1)',
              }}
              padding={{ base: '36px 12px', md: '36px 48px', lg: '36px' }}
              width={{ base: '', md: '500px', lg: '476px' }}
            >
              <Text
                color="black"
                fontWeight="700"
                fontSize={{ base: '32px', md: '48px' }}
                lineHeight={{ base: '39px', md: '60px' }}
                as="h1"
              >
                {t('procurement-service.hero.block1')}
              </Text>
              <Text
                color="black"
                fontWeight="500"
                fontSize={{ base: '14px', md: '18px' }}
                lineHeight={{ base: '20px', md: '22px' }}
                mt={{ base: '18px', md: '18px' }}
              >
                {t.rich('procurement-service.hero.block2', {
                  br: () => <br className="show-mobile" />,
                })}
              </Text>
              <NextLink href={SUPPORT_PROCUREMENT_SERVICE_URL} passHref>
                <Link isExternal _hover={{ textDecor: 'none' }}>
                  <Button
                    variant="brand"
                    width="100%"
                    mt={{ base: '36px', md: '52px' }}
                  >
                    {t('procurement-service.get-started')}
                  </Button>
                </Link>
              </NextLink>
            </Box>
          </Flex>
        </ContainerBase>
      </Center>
      <Box
        height="200px"
        display={{ base: 'block', md: 'none' }}
        bgImage="/img/hero-procurement.png"
        backgroundSize="cover"
        backgroundRepeat="no-repeat"
        backgroundPosition="bottom"
      />
    </Box>
  )
}

const HostingPlacements = () => {
  const t = useTranslations()
  return (
    <ContainerBase mt={{ md: '80px', lg: '116px' }}>
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontWeight="700"
        fontSize={{ base: '32px', md: '48px' }}
        lineHeight={{ base: '39px', md: '50px', lg: '60px' }}
        mt={{ base: '60px', md: '22px', lg: '32px' }}
      >
        {t('procurement-service.hardware-procurement.title')}
      </Text>
      <Box padding={{ md: '0 100px', lg: 0 }}>
        <PromotionSection
          mt={{ base: '52px', md: '65px', lg: '60px' }}
          gap={{ base: '32px', md: '40px', lg: '80px' }}
          contents={[
            {
              media: (
                <BriefCaseIcon
                  color="brand.600"
                  boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                />
              ),
              title: t(
                'procurement-service.hardware-procurement.section1.title'
              ),
              description: t(
                'procurement-service.hardware-procurement.section1.description'
              ),
            },
            {
              media: (
                <RobotIcon
                  color="brand.600"
                  boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                />
              ),
              title: t(
                'procurement-service.hardware-procurement.section2.title'
              ),
              description: t(
                'procurement-service.hardware-procurement.section2.description'
              ),
            },
            {
              media: (
                <CommunicationChannelIcon
                  color="brand.600"
                  boxSize={{ base: '32px', md: '34px', lg: '44px' }}
                />
              ),
              title: t(
                'procurement-service.hardware-procurement.section3.title'
              ),
              description: t(
                'procurement-service.hardware-procurement.section3.description'
              ),
            },
          ]}
        />
      </Box>
      <Divider my={{ base: '60px', md: '106px' }} />
    </ContainerBase>
  )
}

const ContactOurTeam = () => {
  const t = useTranslations()
  return (
    <ContainerBase mb="40px" width={{ base: '100%', md: '768px' }}>
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontWeight="700"
        fontSize={{ base: '32px', md: '48px' }}
        lineHeight={{ base: '39px', md: '50px', lg: '60px' }}
        mt={{ md: '22px', lg: '32px' }}
      >
        {t('procurement-service.contact-team.title')}
      </Text>
      <Text
        textAlign={{ base: 'left', md: 'center' }}
        fontSize={{ base: '14px', md: '20px' }}
        lineHeight={{ base: '20px', md: '25px' }}
        mt={{ base: '40px', md: '22px', lg: '32px' }}
      >
        {t('procurement-service.contact-team.description')}
      </Text>
      <NextLink href={SUPPORT_PROCUREMENT_SERVICE_URL} passHref>
        <Link isExternal _hover={{ textDecor: 'none' }}>
          <Button
            variant="brand"
            mt={{ base: '36px', md: '52px' }}
            width="100%"
          >
            {t('procurement-service.get-started')}
          </Button>
        </Link>
      </NextLink>
    </ContainerBase>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default ProcurementServicePage
